import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as queryString from 'query-string';

import SportLabel from '../../../components/cards/CardComponents/SportLabel';
import { sportTypeIds } from '../../../utils/eventConstants';
import { setShowRating, setShowPower, skipPickleballSurveyV2 } from '../../../actions/onboarding-actions';
import PBRScale from '../../../components/Profile/RatingDisplayV3/PBRScale';
import { colorBallDescriptions, getTennisRatingDescription, getRatingForTennis, getReturnPageName, getPickleballRatingDescriptionV2 } from './constants';

import styles from '../nuoV2.scss';
import withActivateUtr from '../../../components/hocs/withActivateUtr';
import { addTrailingSpace, getRatingDetails, setRatingChoice } from '../../../utils';

const YourRating = (props) => {
    const { nuo, location } = props;
    const { memberSportTypeIds = [] } = nuo?.data || {};
    const profile = useSelector(({ profile }) => profile);
    const auth = useSelector(({ auth }) => auth);

    const parsed = queryString.parse(location.search);
    const shouldGotoReturnPage = !!parsed.return;
    const hasTennisType = memberSportTypeIds.includes(sportTypeIds.TENNIS);
    const hasPickleballType = memberSportTypeIds.includes(sportTypeIds.PICKLEBALL);

    const rating = getRatingDetails(setRatingChoice({ player: profile.data }), false, true, false, false);

    const dispatch = useDispatch();

    const handleNextClick = () => {
        dispatch(setShowRating(false, null));
    };

    const handlePowerClick = () => {
        dispatch(setShowPower(true));
        props.history.push('/');
    };

    const handleSkipClick = async () => {
        if (isPickleballSurveyIncomplete) {
            await dispatch(skipPickleballSurveyV2());
        }
        if (shouldGotoReturnPage) {
            props.history.push(parsed.return);
        } else {
            props.history.push('/');
        }
    };

    const showPickleballRatingSection = hasPickleballType && rating?.pkbRating?.isRated;
    const isPickleballSurveyIncomplete = !auth?.user?.onboardingCompleted && hasPickleballType && !rating?.pkbRating?.isRated;
    const showPowerFooter = auth?.user?.onboardingCompleted && hasTennisType && !rating?.pbrRating && !shouldGotoReturnPage;
    const showNonPowerFooter = auth?.user?.onboardingCompleted && !showPowerFooter;

    const getTitle = () => {
        if (hasTennisType && !showPickleballRatingSection) {
            if (rating?.pbrRating) {
                return 'Your estimated rating';
            }

            return 'Your tennis rating';
        }

        if (hasPickleballType && !hasTennisType) {
            return 'Your pickleball rating';
        }

        return 'Your ratings';
    };

    const getTennisRating = () => {
        if (rating?.pbrRating) {
            // color ball
            return (
                <>
                    <SportLabel sportTypeId={1} label={`CBR`} />
                    <div className={styles.rating}>{rating.pbrRating.ratingDisplay}</div>
                    <div className="mb16">
                        <PBRScale value={rating.pbrRating.value} lightBg />
                    </div>
                    <div className={styles.description}>Based on your responses, your provisional Color Ball Rating is {rating.pbrRating.ratingDisplay}. This rating is ideal for {colorBallDescriptions[rating.pbrRating.value]}. Advance your rating by playing in UTR Sports Color Ball events. See you on the court!</div>
                </>
            );
        }
        // tennis
        const tennisRating = getRatingForTennis(rating, profile);
        return (
            <>
                <SportLabel sportTypeId={1} />
                <div className={styles.rating}>
                    {tennisRating.display}
                </div>
                <div className={styles.description}>
                    Your {addTrailingSpace(tennisRating.prefix)}UTR Rating is {tennisRating.display}. This rating most closely resembles {getTennisRatingDescription(tennisRating.value)} level players. Advance your rating by playing more matches. See you on the courts!
                </div>
            </>
        );
    };

    const getFooter = () => {
        return (
            <>
                {isPickleballSurveyIncomplete && (
                    <div className={styles.ratingFooter}>
                        <div className={styles.title} data-testid="nuo-v2.your-rating.pickleball-prompt">
                            Now let&apos;s get your pickleball rating.
                        </div>
                        <button
                            type="button"
                            className="btn btn-primary btn-block btn-lg text-uppercase"
                            onClick={handleNextClick}
                            data-testid="nuo-v2.your-rating.get-pickleball-rating-btn"
                        >
                            Get Pickleball Rating
                        </button>
                        <div
                            className="link clickable text-center mt8"
                            onClick={handleSkipClick}
                            data-testid="nuo-v2.your-rating.skip-pickleball-link"
                        >
                            {`Not now, take me ${getReturnPageName(parsed.return)}`}
                        </div>
                    </div>
                )}
                {!isPickleballSurveyIncomplete && showPowerFooter && (
                    <div className={styles.ratingFooter}>
                        <div className={styles.title} data-testid="nuo-v2.your-rating.power-prompt">
                            Power your profile
                        </div>
                        <div className={styles.description} data-testid="nuo-v2.your-rating.power-description">
                            Get more out of UTR Sports with a Power Subscription. Benefits include:
                            <ul>
                                <li>See 2 decimal UTR Ratings for everyone</li>
                                <li>Play in unlimited Verified events at a discount</li>
                                <li>Get detailed metrics and results analysis</li>
                                <li>Access priority support</li>
                            </ul>
                        </div>
                        <button
                            type="button"
                            className="btn btn-primary btn-block btn-lg text-uppercase"
                            onClick={handlePowerClick}
                            data-testid="nuo-v2.your-rating.get-power-btn"
                        >
                            Get Power
                        </button>
                        <div
                            className="link clickable text-center mt8"
                            onClick={handleSkipClick}
                            data-testid="nuo-v2.your-rating.skip-power-link"
                        >
                            {`Not now, take me ${getReturnPageName(parsed.return)}`}
                        </div>
                    </div>
                )}
                {!isPickleballSurveyIncomplete && showNonPowerFooter && (
                    <div className={styles.ratingFooter}>
                        <button
                            type="button"
                            className="btn btn-primary btn-block btn-lg text-uppercase"
                            onClick={handleSkipClick}
                            data-testid="nuo-v2.your-rating.go-home-btn"
                        >
                            {`Go ${getReturnPageName(parsed?.return)}`}
                        </button>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className={styles.pageContent}>
            <div className={styles.title} data-testid="nuo-v2.your-rating.title">
                {getTitle()}
            </div>
            <div className="row">
                {hasTennisType && (
                    <div className={`col-${showPickleballRatingSection ? '6' : '12'}`}>
                        <div className={styles.ratingContainer}>
                            {getTennisRating()}
                        </div>
                    </div>
                )}
                {showPickleballRatingSection && (
                    <div className={`col-${hasTennisType ? '6' : '12'}`}>
                        <div className={styles.ratingContainer}>
                            <SportLabel sportTypeId={2} />
                            <div className={styles.rating}>{rating?.pkbRating?.value}</div>
                            <div className={styles.description}>Based on your responses, you have been assigned a {rating?.pkbRating?.value} rating. This rating most closely resembles {getPickleballRatingDescriptionV2(rating?.pkbRating?.value)} level players. Meet and compete with other players by joining a UTR Sports Pickleball event. See you on the court!</div>
                        </div>
                    </div>
                )}
            </div>
            <hr />
            {getFooter()}
        </div>
    );
};

export default withRouter(withActivateUtr(YourRating));
